import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { CloseIcon } from '@atgfr8/icons';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { ToastType } from '@components';
import { Button } from '@components/Button';
import {
  carrierReactivationFromLoad,
  carrierReactivationFromProfile,
  CarrierReactivationRequestCacheItemT,
} from '@store/carriers';
import { closeModal, isModalOpen } from '@store/modals';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { handleHeaderSave } from '@views/order/components/orderPageHeader/helpers';
import { get } from 'lodash';
import * as yup from 'yup';

const MAX_COMMENT = 1000;

const formSchema = yup.object({
  comment: yup
    .string()
    .max(MAX_COMMENT, `Comment must be ${MAX_COMMENT} characters or less.`),
});

type Fields = {
  comment: string;
};

type Props = {
  modalId: string;
  carrierId: number | null | undefined;
  loadId?: number;
  onRequested: (item: CarrierReactivationRequestCacheItemT) => void;
  changeTrackingSource?: ChangeTrackingSource;
};

export const ReactivationRequestModal = (props: Props) => {
  const { form, data, errors, setFields, reset, isSubmitting } =
    createForm<Fields>({
      initialValues: { comment: '' },
      extend: validator({ schema: formSchema }),
      onSubmit: async (values) => {
        const trimmed = values.comment.trim();
        if (props.carrierId == null) {
          handleToast(ToastType.Error, 'Carrier ID is required.');
          return;
        }
        const carrierId = props.carrierId;
        let item: CarrierReactivationRequestCacheItemT | null = null;
        if (props.loadId == null) {
          item =
            (await carrierReactivationFromProfile(
              carrierId,
              trimmed,
              props.changeTrackingSource,
            )) ?? null;
        } else {
          await handleHeaderSave(undefined, undefined);
          item =
            (await carrierReactivationFromLoad(
              carrierId,
              props.loadId,
              trimmed,
              props.changeTrackingSource,
            )) ?? null;
        }
        if (Boolean(item)) {
          props.onRequested(item!);
          handleClose();
        }
      },
      onError: (error) => {
        handleToast(ToastType.Error, (error as Error).message);
      },
    });

  const handleClose = () => {
    reset();
    closeModal(props.modalId);
  };

  return (
    <Dialog
      id={props.modalId}
      title="Request Reactivation"
      class="w-[30vw] min-w-105 rounded-[5px] bg-white"
      onClose={handleClose}
      open={isModalOpen(props.modalId)}
      noCloseOnClickOutside={isSubmitting()}
    >
      <DialogTitle class="flex flex-row">
        <div class="flex-1">Request Reactivation</div>
        <div>
          <span
            class={
              isSubmitting()
                ? 'pointer-events-none opacity-50'
                : 'cursor-pointer'
            }
            onClick={() => !isSubmitting() && handleClose()}
          >
            <CloseIcon size={20} color="rgba(0, 0, 0, 0.54)" />
          </span>
        </div>
      </DialogTitle>
      <form ref={form} class="w-full">
        <DialogContent>
          <TextField
            label="Comments (Optional)"
            placeholder="Enter comments to be reviewed by compliance."
            dataTestId="reactivation-comment-input"
            value={data().comment}
            multiline
            rows={4}
            maxLength={MAX_COMMENT}
            style={{ resize: 'none' }}
            error={get(errors(), 'comment[0]')}
            disabled={isSubmitting()}
            onChange={(v: string) => setFields('comment', v)}
          />
        </DialogContent>
        <DialogActions class="mb-2 pr-2">
          <Button
            isLoading={isSubmitting()}
            disabled={isSubmitting()}
            variant="contained"
            label="Submit"
            type="submit"
            dataTestId={formatDataTestId('Submit Reactivation', 'button')}
          />
          <Button
            class="float-right"
            variant="text"
            label="Cancel"
            type="button"
            disabled={isSubmitting()}
            onClick={handleClose}
            dataTestId={formatDataTestId('Cancel Reactivation', 'button')}
          />
        </DialogActions>
      </form>
    </Dialog>
  );
};
